
'use client';

import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react';
import { useAuth } from './auth-context';
import { useFirebase } from '@/firebase';
import { collection, doc, setDoc, deleteDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';

// Narrower shape than the canonical `Product` in product-context.tsx — the
// wishlist only persists what the saved-items UI renders (id, name, price,
// image URL). Callers strip the full Product down before passing it in.
export interface WishlistItem {
  id: string;
  name: string;
  price: number;
  image: string;
}

export interface SavedItem {
  product: WishlistItem;
  addedAt: Date;
}

interface WishlistContextType {
  saved: SavedItem[];
  toggleSave: (product: WishlistItem, options?: { silent?: boolean }) => void;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export function WishlistProvider({ children }: { children: ReactNode }) {
  const [saved, setSaved] = useState<SavedItem[]>([]);
  const { authUser } = useAuth();
  const { firestore } = useFirebase();

  // Sync saved items from Firestore when user logs in
  useEffect(() => {
    if (!authUser || !firestore) {
      setSaved([]);
      return;
    }

    const savedRef = collection(firestore, 'users', authUser.uid, 'savedProducts');
    const unsubscribe = onSnapshot(savedRef, (snapshot) => {
      const items: SavedItem[] = snapshot.docs.map(d => {
        const data = d.data();
        return {
          product: {
            id: d.id,
            name: data.name,
            price: data.price,
            image: data.image,
          },
          addedAt: data.addedAt?.toDate ? data.addedAt.toDate() : new Date(data.addedAt || Date.now()),
        };
      });
      setSaved(items);
    });

    return () => unsubscribe();
  }, [authUser, firestore]);

  const toggleSave = useCallback(async (product: WishlistItem, options: { silent?: boolean } = {}) => {
    if (!authUser || !firestore) {
      // Unauthenticated: local-only toggle
      setSaved(prev => {
        const isAlreadySaved = prev.some(item => item.product.id === product.id);
        if (isAlreadySaved) return prev.filter(item => item.product.id !== product.id);
        return [...prev, { product, addedAt: new Date() }];
      });
      return;
    }

    const savedRef = doc(firestore, 'users', authUser.uid, 'savedProducts', product.id);
    const isAlreadySaved = saved.some(item => item.product.id === product.id);

    if (isAlreadySaved) {
      await deleteDoc(savedRef);
    } else {
      await setDoc(savedRef, {
        productId: product.id,
        name: product.name,
        price: product.price,
        image: product.image,
        addedAt: serverTimestamp(),
      });
    }
  }, [authUser, firestore, saved]);

  const value = { saved, toggleSave };

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
}

export function useWishlist() {
  const context = useContext(WishlistContext);
  if (context === undefined) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
}
